import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import { Check, AlertTriangle, TrendingUp, Mail, Phone, ExternalLink } from "lucide-react";
import { aud, CONTACT } from "@/lib/api";
import { track } from "@/lib/analytics";
import CalendlyEmbed from "@/components/valuation/CalendlyEmbed";

const DISCLAIMER =
  "This is an indicative estimate based on the information provided and general market assumptions. The calculation uses the reported net profit and owner remuneration without reviewing financial records or making detailed accounting adjustments. It is not a formal business valuation or a guarantee of the price that may be achieved. Business value may be affected by financial records, lease terms, staffing, buyer demand and other factors.";

const Cell = ({ label, value }) => (
  <div className="border-b border-r border-border p-5">
    <div className="eyebrow text-muted-foreground mb-2">{label}</div>
    <div className="text-base md:text-lg font-medium leading-snug">{value}</div>
  </div>
);

const List = ({ title, items, icon: Icon, tone }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      <Icon size={16} className={tone} />
      <h3 className="eyebrow">{title}</h3>
    </div>
    <ul className="space-y-3">
      {items.map((t, i) => (
        <li key={i} className="flex gap-3 text-muted-foreground leading-relaxed">
          <span className={`mt-2 w-1.5 h-1.5 rounded-full shrink-0 ${tone.replace("text-", "bg-")}`} />
          {t}
        </li>
      ))}
    </ul>
  </div>
);

export default function Report({ report, firstName }) {
  const r = report;
  const methodNote =
    r.recommended_method === "asset"
      ? "Based on your fit-out value plus one year's earnings"
      : "Based on your earnings multiple";

  // Fire the valuation-completed conversion when the report displays (exactly once)
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    track("Salon_valuation_complete", {
      currency: "AUD",
      value: r.recommended_value || 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="w-full max-w-4xl mx-auto"
      data-testid="report"
    >
      <div className="eyebrow text-brand mb-4">
        {firstName ? `${firstName}, here's your result` : "Your result"}
      </div>

      {/* Headline value */}
      <div className="mb-3">
        <div className="eyebrow text-muted-foreground mb-3">Indicative value</div>
        <div className="font-serif font-light text-6xl md:text-8xl tracking-tight text-brand leading-none" data-testid="report-value">
          {aud(r.recommended_value)}
        </div>
        <div className="mt-4 text-lg text-muted-foreground">
          Range {aud(r.valuation_low)} &ndash; {aud(r.valuation_high)} &middot; {methodNote}
        </div>
      </div>

      {/* Earnings vs asset cross-check */}
      <div className="grid sm:grid-cols-2 border-t border-l border-border mt-10">
        <Cell label="Earnings multiple result" value={aud(r.valuation_central)} />
        <Cell label="Fit-out + one year's earnings" value={aud(r.asset_based_value)} />
      </div>

      {/* Data grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-l border-border mt-8">
        <Cell label="Owner earnings (SDE)" value={aud(r.sde)} />
        <Cell label="Central estimate" value={aud(r.valuation_central)} />
        <Cell label="Saleability score" value={`${r.saleability_score ?? "—"} / 100`} />
        <Cell label="Applied multiple" value={`${r.multiple_low}x \u2013 ${r.multiple_high}x`} />
        <Cell label="SDE margin" value={`${(r.sde_margin * 100).toFixed(1)}% \u00b7 ${r.sde_margin_category}`} />
        <Cell label="Owner reliance" value={r.owner_reliance_assessment} />
        <Cell label="Team reliance" value={r.team_reliance_assessment} />
        <Cell label="Station utilisation" value={r.station_utilisation_text} />
        <Cell label="Unused capacity" value={r.capacity_assessment} />
        <Cell label="Lease security" value={r.lease_assessment} />
        <Cell label="Occupancy cost" value={`${(r.occupancy_cost_pct * 100).toFixed(1)}% of revenue`} />
        <Cell label="Fit-out" value={r.fitout_assessment} />
        <Cell label="Value band" value={r.band_label} />
      </div>

      {/* Strengths / risks / actions */}
      <div className="grid md:grid-cols-3 gap-10 mt-16">
        <List title="Key strengths" items={r.strengths} icon={Check} tone="text-brand" />
        <List
          title="Key considerations"
          items={r.risks.length ? r.risks : ["No major risks flagged"]}
          icon={AlertTriangle}
          tone="text-secondary"
        />
        <List title="Ways to improve value" items={r.actions} icon={TrendingUp} tone="text-brand" />
      </div>

      {/* Booking CTA with inline Calendly */}
      <div className="mt-16" data-testid="report-booking">
        <div className="bg-primary text-primary-foreground grain relative p-8 md:p-10">
          <div className="relative z-[2] flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <div className="eyebrow text-brand mb-3">Book a free appraisal</div>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight">
                Pick a time that suits you
              </h3>
              <p className="text-primary-foreground/70 mt-2 max-w-md">
                Choose a time below for a confidential, no-cost appraisal &mdash;
                or call us directly.
              </p>
            </div>
            <a
              href={CONTACT.phoneTel}
              data-testid="report-call"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-brand text-brand-foreground pl-6 pr-5 py-4 eyebrow hover:bg-white hover:text-primary transition-colors duration-300 whitespace-nowrap shrink-0"
            >
              <Phone size={16} /> Call {CONTACT.phone}
            </a>
          </div>
        </div>

        <div className="border-x border-b border-border bg-white">
          <CalendlyEmbed url={CONTACT.calendly} trackingValue={r.recommended_value} prefill={{ name: firstName }} />
        </div>

        <div className="flex flex-wrap items-center gap-6 mt-4">
          <a
            href={CONTACT.calendly}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="report-book"
            className="inline-flex items-center gap-2 text-brand hover:opacity-70 transition-opacity text-sm"
          >
            <ExternalLink size={14} /> Open booking in a new tab
          </a>
          <a
            href={`mailto:${CONTACT.email}`}
            data-testid="report-cta"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-brand transition-colors text-sm"
          >
            <Mail size={14} /> Or email {CONTACT.email}
          </a>
        </div>
      </div>

      <p className="text-xs text-muted-foreground leading-relaxed mt-10">{DISCLAIMER}</p>
      <p className="text-sm text-muted-foreground mt-6">
        A copy of this report has been emailed to you.
      </p>
    </motion.div>
  );
}
